import { r as registerInstance, f as forceUpdate, h, H as Host, g as getElement } from './index-7e9696f3.js';

var NotificationPosition;
(function (NotificationPosition) {
  NotificationPosition["TOP_LEFT"] = "top-left";
  NotificationPosition["TOP_CENTER"] = "top-center";
  NotificationPosition["TOP_RIGHT"] = "top-right";
  NotificationPosition["BOTTOM_CENTER"] = "bottom-center";
  NotificationPosition["BOTTOM_LEFT"] = "bottom-left";
  NotificationPosition["BOTTOM_RIGHT"] = "bottom-right";
})(NotificationPosition || (NotificationPosition = {}));

var NotificationType;
(function (NotificationType) {
  NotificationType["Error"] = "error";
  NotificationType["Info"] = "info";
  NotificationType["Success"] = "success";
  NotificationType["Warning"] = "warning";
  NotificationType["None"] = "none";
})(NotificationType || (NotificationType = {}));

const notificationCss = ":host{padding:16px;font-family:Figtree, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, Oxygen, Ubuntu, Cantarell, \"Fira Sans\", \"Droid Sans\", \"Helvetica Neue\", Arial, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\";position:fixed;z-index:1000;display:flex;flex-direction:column}:host(.top-left){top:0;left:0}:host(.top-center){top:0;align-items:center;left:50%;transform:translate(-50%, 0)}:host(.top-right){top:0;right:0;align-items:flex-end}:host(.bottom-left){bottom:0;left:0}:host(.bottom-center){bottom:0;align-items:center;left:50%;transform:translate(-50%, 0)}:host(.bottom-right){bottom:0;right:0;align-items:flex-end}.notification{margin-top:16px;display:flex;flex-direction:row;box-shadow:0px 4px 4px rgba(0, 0, 0, 0.16), 0px 2px 2px rgba(0, 0, 0, 0.18), 0px 1px 1px rgba(0, 0, 0, 0.2);border-radius:2px;background-color:#fcfcfc;overflow:hidden}.notification .icon{width:48px;display:flex;justify-content:center;align-items:center}.notification .icon-type--error{background-color:#ef4444}.notification .icon-type--success{background-color:#22c55e}.notification .icon-type--warning{background-color:#f59e0b}.notification .icon-type--info{background-color:#6b7280}.notification .label{padding-left:16px;flex:1;font-size:0.875rem}.notification .btn{padding-left:24px;padding-right:16px;display:flex;align-items:center;border:none;background-color:#fcfcfc}.notification .btn:focus{outline:none;box-shadow:none}";

const Notification = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    /**
     * List of currently displayed notifications.
     *
     * @type {NotificationMessage[]}
     */
    this.notifications = [];
    /**
     * Used for setting a unique identifier for each notification.
     *
     * @type {number}
     */
    this.notificationId = 0;
    this.position = NotificationPosition.BOTTOM_RIGHT;
    this.duration = 3;
  }
  /**
   * Show a notification.
   *
   * @param {string} message - Message to display.
   * @param {string} [type='none'] - Type of notification. Available types: 'info', 'warning', 'success', 'error' and 'none'.
   * @param {boolean} [sticky=false] - Set message as sticky to prevent it from disappearing.
   * @returns {Promise<void>}
   */
  async push(message, type = 'none', sticky = false) {
    if (typeof message !== 'string' || message.length < 1) {
      return;
    }
    // Check validity of type
    const typeExist = Object.values(NotificationType)
      .some((notificationType) => notificationType === type);
    if (!typeExist) {
      // eslint-disable-next-line no-console
      console.error('Invalid notification type');
      return;
    }
    const notificationMessage = {
      id: this.notificationId,
      message: message,
      sticky: sticky,
      type: type,
    };
    if (sticky === false) {
      notificationMessage.timer = window.setTimeout(() => this.dismiss(notificationMessage.id), (this.duration * 1000));
    }
    this.notificationId++;
    this.notifications.push(notificationMessage);
    forceUpdate(this.hostElement);
  }
  /**
   * Clear all notifications.
   *
   * @returns {Promise<void>}
   */
  async clearAll() {
    this.notifications = [];
    forceUpdate(this.hostElement);
  }
  /**
   * Dismiss a single notification.
   *
   * @param {number} id
   */
  dismiss(id) {
    this.notifications = this.notifications.filter((notification) => notification.id !== id);
    forceUpdate(this.hostElement);
  }
  render() {
    return (h(Host, { class: this.position }, this.notifications.map((notification) => this.renderNotification(notification))));
  }
  /**
   * Get JSX for notification.
   *
   * @param {NotificationMessage} { id, message }
   * @returns {JSX.Element}
   */
  renderNotification({ id, message, type }) {
    return (h("div", { class: "notification", role: "alert" }, type && type !== 'none' ? this.renderIcon(type) : null, h("p", { class: "label" }, message), h("button", { type: "button", "aria-label": "Dismiss notification", class: "btn", onClick: () => this.dismiss(id) }, h("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-hidden": "true", focusable: "false" }, h("path", { d: "M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z", fill: "#1E2025" })))));
  }
  /**
   * Get JSX for type-icon.
   *
   * @param {NotificationType} type
   * @returns {JSX.Element}
   */
  renderIcon(type) {
    if (type === NotificationType.Error) {
      return (h("div", { class: "icon icon-type--error" }, h("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-hidden": "true", focusable: "false" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM9 15V13H11V15H9ZM9 5V11H11V5H9Z", fill: "#FCFCFC" }))));
    }
    else if (type === NotificationType.Success) {
      return (h("div", { class: "icon icon-type--success" }, h("svg", { width: "18", height: "14", viewBox: "0 0 18 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-hidden": "true", focusable: "false" }, h("path", { d: "M5.99997 11.17L1.82997 7L0.409973 8.41L5.99997 14L18 2L16.59 0.59L5.99997 11.17Z", fill: "#FCFCFC" }))));
    }
    else if (type === NotificationType.Warning) {
      return (h("div", { class: "icon icon-type--warning" }, h("svg", { width: "22", height: "19", viewBox: "0 0 22 19", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-hidden": "true", focusable: "false" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M22 19L11 0L0 19H22ZM10 16V14H12V16H10ZM10 12H12V8H10V12Z", fill: "#FCFCFC" }))));
    }
    return (h("div", { class: "icon icon-type--info" }, h("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-hidden": "true", focusable: "false" }, h("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM9 15V9H11V15H9ZM9 5V7H11V5H9Z", fill: "#FCFCFC" }))));
  }
  get hostElement() { return getElement(this); }
};
Notification.style = notificationCss;

export { Notification as mi_notification };
